import { HelpBox } from 'components/v2/help-boxes';
import { toaster } from 'components/v2/toast';
import { Button } from 'primereact/button';
import React, { useState } from 'react';
import { QUERY_KEYS } from 'services/api/constants';
import { autofillAdditionalVoluntary15804Nd } from 'services/api/epdLcaResultsApi';
import styled from 'styled-components';
import queryClient from 'util/queryClient';
import { PoorMansError } from 'util/utils';
import { useNotifyLcaImported } from '../../grids/lca-results/util/LcaImportContext';

interface Props {
  epdVersionId: string;
  disabled?: boolean;
}

const LcaResultsAutofillNdAdditionalVoluntary15804: React.FC<Props> = ({ epdVersionId, disabled }) => {
  const [isLoading, setIsLoading] = useState(false);
  const notifyImported = useNotifyLcaImported();

  const onAutofillNd = () => {
    setIsLoading(true);
    autofillAdditionalVoluntary15804Nd(epdVersionId)
      .then(async () => {
        setIsLoading(false);
        await Promise.allSettled([
          queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_LCA_RESULTS] }),
          queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_ACTIONS_STATES] }),
        ]);
        notifyImported();
      })
      .catch((exception) => {
        setIsLoading(false);
        const errors = PoorMansError(exception);
        toaster({
          severity: 'error',
          summary: 'Error',
          details: errors ? errors : 'Error autofilling ND',
        });
      });
  };

  return (
    <StyledHelpBox>
      <span>
        Following EN 15804, enter numeric values for the declared indicators and "ND" for optional indicators that are not
        declared. You can use the "Fill blanks with ND" button to automatically populate empty cells.
      </span>
      <StyledFillBlanksButton onClick={onAutofillNd} disabled={isLoading || disabled}>
        {isLoading ? 'Processing...' : 'Fill blanks with ND'}
      </StyledFillBlanksButton>
    </StyledHelpBox>
  );
};

const StyledHelpBox = styled(HelpBox)`
  flex-direction: row;
  gap: 8px;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 16px;
`;

const StyledFillBlanksButton = styled(Button)`
  background-color: '#CC302B';
  border-color: '#CC302B';
  min-width: 175px;
  border-radius: 4px;
  font-size: 1rem;
  padding: 0.5rem 1rem;
  justify-content: center;

  &:disabled {
    background-color: ${(props) => props.theme.colors.elementsButtonDisabled};
    border-color: ${(props) => props.theme.colors.elementsButtonDisabled};
  }

  height: 1.5rem;
  min-height: 22px;
  ${(props) => props.theme.typography.buttonButtonDefault};
`;

export default LcaResultsAutofillNdAdditionalVoluntary15804;
